import { useState, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { IAvailablePhoneNumber, IPhoneNumber } from '../../interface'
import { formatPhoneNumber, getGroupedPhoneNumbers } from '../../utils'
import { CloseIcon } from '@forwardflow/icons'
import { PhoneNumberSearchInput } from '../PhoneNumberSearchInput'
import { PhoneNumbers } from '../PhoneNumbers'
import { PhoneNumbersSkeleton } from '../PhoneNumbersSkeleton'
import { ConfirmPurchaseNumbersModal } from '../modals/ConfirmPurchaseNumbersModal'
import {
   useGetAvailablePhoneNumbers,
   usePurchasePhoneNumbers,
} from '../../queries/phone-number-management.queries'
import { useAppStore } from '@forwardflow/store'
import { message } from 'antd'

/**
 * Converts an IAvailablePhoneNumber to IPhoneNumber shape
 * so it can be rendered by the PhoneNumbers component.
 */
const toPhoneNumber = (available: IAvailablePhoneNumber): IPhoneNumber => ({
   phoneNumber: {
      phoneNumber: available.phoneNumber,
      friendlyName: available.phoneNumber,
      lata: '',
      locality: available.locality || '',
      rateCenter: '',
      latitude: null,
      longitude: null,
      region: '',
      postalCode: null,
      isoCountry: 'US',
      addressRequirements: '',
      beta: false,
      capabilities: { voice: true, SMS: true, MMS: false },
      status: 'available',
   },
   lastUpdated: '',
   campaignCount: 0,
   campaignIds: [],
   campaigns: [],
   engagedConversations: 0,
   conversationStats: {
      avgResponseRate: 0,
      totalMessages: 0,
      totalIncoming: 0,
      totalOutgoing: 0,
   },
})

interface BuyNewNumbersViewProps {
   setSelectedTab?: (tab: string) => void
   onNumbersSelected?: (numbers: IAvailablePhoneNumber[]) => void
   onConfirmPurchase?: (numbers: IAvailablePhoneNumber[]) => Promise<void>
}

export const BuyNewNumbersView = ({
   setSelectedTab,
   onNumbersSelected,
   onConfirmPurchase,
}: BuyNewNumbersViewProps) => {
   const { currentOrganization } = useAppStore()
   const [searchQuery, setSearchQuery] = useState('')
   const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
   const [selectedNumbers, setSelectedNumbers] = useState<
      IAvailablePhoneNumber[]
   >([])
   const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
   const [isPurchasing, setIsPurchasing] = useState(false)

   const { purchasePhoneNumbers } = usePurchasePhoneNumbers()

   // Debounce search query
   useEffect(() => {
      const timer = setTimeout(() => {
         setDebouncedSearchQuery(searchQuery)
      }, 500)

      return () => clearTimeout(timer)
   }, [searchQuery])

   const { data: availableNumbers = [], isLoading: isLoadingAvailableNumbers } =
      useGetAvailablePhoneNumbers(
         debouncedSearchQuery
            ? { limit: 1000, search: debouncedSearchQuery }
            : { limit: 50 }
      ) as { data: IAvailablePhoneNumber[]; isLoading: boolean }

   // Convert available numbers to grouped IPhoneNumberGroup[] for PhoneNumbers component
   const groupedAvailableNumbers = useMemo(() => {
      const adapted = availableNumbers.map(toPhoneNumber)
      return getGroupedPhoneNumbers(adapted)
   }, [availableNumbers])

   // Convert selectedNumbers to IPhoneNumber[] for PhoneNumbers selectedNumbers prop
   const selectedAsIPhoneNumbers = useMemo(
      () => selectedNumbers.map(toPhoneNumber),
      [selectedNumbers]
   )

   const handleSelectNumber = (number: IPhoneNumber) => {
      const phoneNum = number.phoneNumber.phoneNumber
      const isSelected = selectedNumbers.some(
         (n) => n.phoneNumber === phoneNum
      )

      const updatedNumbers = isSelected
         ? selectedNumbers.filter((n) => n.phoneNumber !== phoneNum)
         : [
              ...selectedNumbers,
              {
                 phoneNumber: phoneNum,
                 locality: number.phoneNumber.locality,
              },
           ]

      setSelectedNumbers(updatedNumbers as IAvailablePhoneNumber[])
      onNumbersSelected?.(updatedNumbers as IAvailablePhoneNumber[])
   }

   const handleOpenConfirmModal = async () => {
      if (selectedNumbers.length === 0) return

      if (onConfirmPurchase) {
         setIsPurchasing(true)
         try {
            await onConfirmPurchase(selectedNumbers)
            setSelectedTab?.('list-of-numbers')
            setSelectedNumbers([])
         } catch (error) {
            console.error('Error purchasing phone numbers:', error)
            message.error('Failed to add phone numbers. Please try again.')
         } finally {
            setIsPurchasing(false)
         }
      } else {
         setIsConfirmModalOpen(true)
      }
   }

   const handleConfirmPurchase = async () => {
      setIsPurchasing(true)
      try {
         const orgId = currentOrganization || ''
         const phoneNumbersToPurchase = selectedNumbers.map(
            (number) => number.phoneNumber
         )

         await purchasePhoneNumbers(orgId, phoneNumbersToPurchase)

         message.success(
            `Successfully purchased ${phoneNumbersToPurchase.length} phone number${
               phoneNumbersToPurchase.length !== 1 ? 's' : ''
            }`
         )

         setIsConfirmModalOpen(false)
         setSelectedTab?.('list-of-numbers')
         setSelectedNumbers([])
      } catch (error) {
         console.error('Error purchasing phone numbers:', error)
         message.error('Failed to purchase phone numbers. Please try again.')
      } finally {
         setIsPurchasing(false)
      }
   }

   const handleCloseModal = () => {
      if (!isPurchasing) {
         setIsConfirmModalOpen(false)
      }
   }

   return (
      <div className="flex flex-col h-full">
         {/* Fixed Header Section */}
         <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col gap-y-6 flex-shrink-0"
         >
            <div className="p-4 bg-white/[0.05] mr-3 rounded-lg space-y-5">
               <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <p className="text-white text-sm font-medium">
                     Purchase new phone numbers
                  </p>
                  <div className="flex items-center gap-x-1.5">
                     <motion.button
                        key="cancel-button"
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                           setSelectedTab?.('list-of-numbers')
                           setSelectedNumbers([])
                        }}
                        disabled={isPurchasing}
                        className="bg-black/20 rounded-md px-4 py-1.5 text-white text-xs font-medium hover:bg-black/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                        Cancel
                     </motion.button>
                     <motion.button
                        disabled={selectedNumbers.length === 0 || isPurchasing}
                        key="confirm-button"
                        whileTap={{ scale: 0.95 }}
                        onClick={handleOpenConfirmModal}
                        className="bg-[#9061DA66] rounded-md px-4 py-1.5 text-white text-xs font-medium hover:bg-black/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-x-1.5 justify-center"
                     >
                        {isPurchasing && (
                           <motion.div
                              animate={{ rotate: 360 }}
                              transition={{
                                 duration: 1,
                                 repeat: Infinity,
                                 ease: 'linear',
                              }}
                              className="w-3 h-3 border-2 border-white border-t-transparent rounded-full"
                           />
                        )}
                        {isPurchasing ? 'Processing...' : 'Confirm'}
                     </motion.button>
                  </div>
               </div>
               {/* Selected Numbers Section */}
               {selectedNumbers.length > 0 ? (
                  <div className="pr-3">
                     <p className="text-white text-sm font-medium mb-3">
                        Selected Numbers ({selectedNumbers.length})
                     </p>
                     <div className="flex flex-wrap gap-2">
                        {selectedNumbers.map((number) => (
                           <div
                              key={number.phoneNumber}
                              className="flex items-center gap-x-2 bg-[#D9D9D9]/10 rounded-lg px-4 py-2 pr-2"
                           >
                              <span className="text-white text-sm">
                                 {formatPhoneNumber(number.phoneNumber)}
                              </span>
                              <button
                                 onClick={() =>
                                    handleSelectNumber(toPhoneNumber(number))
                                 }
                                 className="text-white/60 hover:text-white"
                              >
                                 <CloseIcon className="w-3 h-3" />
                              </button>
                           </div>
                        ))}
                     </div>
                  </div>
               ) : (
                  <div className="flex items-center justify-center py-6">
                     <p className="text-white/40 text-sm">
                        No numbers selected
                     </p>
                  </div>
               )}
            </div>

            <PhoneNumberSearchInput
               value={searchQuery}
               onChange={setSearchQuery}
               placeholder="Search by area code or location"
            />

            <div className="flex items-center justify-between pr-3 mt-6">
               <p className="text-white text-sm font-medium">
                  Available numbers to purchase
               </p>
            </div>
         </motion.div>

         {/* Scrollable List Section */}
         <div className="flex-1 min-h-0 overflow-y-auto mt-2">
            {isLoadingAvailableNumbers ? (
               <PhoneNumbersSkeleton groups={3} itemsPerGroup={2} />
            ) : groupedAvailableNumbers.length > 0 ? (
               <PhoneNumbers
                  numberGroups={groupedAvailableNumbers}
                  showMessageCount={false}
                  selectMode={true}
                  onSelect={handleSelectNumber}
                  selectedNumbers={selectedAsIPhoneNumbers}
               />
            ) : (
               <div className="flex items-center justify-center py-12">
                  <p className="text-white/40 text-sm">
                     {searchQuery
                        ? `No available numbers found for "${searchQuery}"`
                        : 'Search for available phone numbers to purchase'}
                  </p>
               </div>
            )}
         </div>

         {/* Confirmation Modal */}
         <ConfirmPurchaseNumbersModal
            isOpen={isConfirmModalOpen}
            onClose={handleCloseModal}
            onConfirm={handleConfirmPurchase}
            numberCount={selectedNumbers.length}
            isLoading={isPurchasing}
         />
      </div>
   )
}
